/**
 * SidebarProjectItemChrome — shared row chrome used by every sidebar project item.
 *
 * Both SidebarCloudProjectItem and SidebarProjectItem render the same outer
 * structure:
 *
 *   [icon→chevron on hover]  name  | (absolute right) status dot + action buttons
 *
 * This component owns that chrome so the two item components only supply their
 * environment-specific slots and event handlers. Dialogs, thread lists, and
 * other per-environment content go in `children`.
 *
 * Slot contract:
 *   iconSlot   — content of the left icon area (hidden on hover, replaced by
 *                the chevron). Cloud passes a static CloudIcon; local passes
 *                ProjectFavicon + an optional collapsed-status badge.
 *   rightSlot  — absolutely-positioned right side: status dot + action button(s).
 *   children   — rendered after the SidebarMenuButton: AlertDialogs, sheets, etc.
 */

import { memo, type ReactNode } from "react";
import React from "react";
import { ChevronRightIcon } from "lucide-react";
import { SidebarMenuButton, SidebarMenuItem } from "../ui/sidebar";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import type { SortableProjectHandleProps } from "./SidebarProjectItem";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SidebarProjectItemChromeProps {
  // Slots
  iconSlot: ReactNode;
  name: string;
  nameTooltip: string;
  rightSlot: ReactNode;

  // Chevron
  isExpanded?: boolean;

  // Row state → className modifiers
  isActive?: boolean;
  isBusy?: boolean; // opacity-70 + no pointer events (connecting/removing)
  isMissing?: boolean; // opacity-55 hover:opacity-90 (project not on disk)

  // SidebarMenuButton events
  onClick: React.MouseEventHandler;
  onContextMenu?: React.MouseEventHandler;
  onPointerDownCapture?: React.PointerEventHandler;
  onKeyDown?: React.KeyboardEventHandler;
  ariaExpanded?: boolean;

  // DnD drag handle (local sort only — cloud rows don't drag)
  isManualProjectSorting?: boolean;
  dragHandleProps?: SortableProjectHandleProps | null;

  // Data attributes forwarded to the root container div
  rootDataAttributes?: Record<string, string>;

  // Dialogs, sheets, thread list, etc.
  children?: ReactNode;
}

// ---------------------------------------------------------------------------
// SidebarProjectItemChrome
// ---------------------------------------------------------------------------

export const SidebarProjectItemChrome = memo(function SidebarProjectItemChrome({
  iconSlot,
  name,
  nameTooltip,
  rightSlot,
  isExpanded = false,
  isActive = false,
  isBusy = false,
  isMissing = false,
  onClick,
  onContextMenu,
  onPointerDownCapture,
  onKeyDown,
  ariaExpanded,
  isManualProjectSorting = false,
  dragHandleProps,
  rootDataAttributes,
  children,
}: SidebarProjectItemChromeProps) {
  return (
    <SidebarMenuItem>
      <div
        className="group/project-header relative min-w-0 rounded-md"
        onContextMenu={onContextMenu}
        {...rootDataAttributes}
      >
        <SidebarMenuButton
          ref={
            isManualProjectSorting && dragHandleProps
              ? dragHandleProps.setActivatorNodeRef
              : undefined
          }
          render={<div role="button" tabIndex={0} />}
          size="sm"
          aria-expanded={ariaExpanded}
          aria-busy={isBusy || undefined}
          className={`h-auto min-h-8 gap-2 overflow-hidden rounded-md px-2 py-1.5 pr-14 text-left transition-colors hover:bg-accent/65 group-hover/project-header:bg-accent/65 group-hover/project-header:text-sidebar-accent-foreground ${
            isManualProjectSorting ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
          } ${isActive ? "bg-accent/65 text-sidebar-accent-foreground" : ""} ${
            isBusy ? "pointer-events-none opacity-70" : ""
          } ${isMissing ? "opacity-55 hover:opacity-90" : ""}`}
          {...(isManualProjectSorting && dragHandleProps ? dragHandleProps.attributes : {})}
          {...(isManualProjectSorting && dragHandleProps ? dragHandleProps.listeners : {})}
          onPointerDownCapture={onPointerDownCapture}
          onClick={onClick}
          onKeyDown={onKeyDown}
        >
          {/* Slot 1: icon (hidden on hover) ↔ chevron (shown on hover) */}
          <span className="relative inline-flex size-3.5 shrink-0 items-center justify-center">
            <span className="absolute inset-0 inline-flex items-center justify-center transition-opacity duration-150 group-hover/project-header:opacity-0 group-focus-within/project-header:opacity-0">
              {iconSlot}
            </span>
            <ChevronRightIcon
              className={`size-3.5 text-muted-foreground/55 opacity-0 transition-[opacity,transform] duration-150 group-hover/project-header:opacity-100 group-focus-within/project-header:opacity-100 ${
                isExpanded ? "rotate-90" : ""
              }`}
            />
          </span>

          {/* Project name */}
          <Tooltip>
            <TooltipTrigger
              render={
                <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
                  {name}
                </span>
              }
            />
            <TooltipPopup side="top">{nameTooltip}</TooltipPopup>
          </Tooltip>
        </SidebarMenuButton>

        {/* Slot 2: right side — status dot + action buttons */}
        <div className="absolute top-1/2 right-1.5 inline-flex h-5 -translate-y-1/2 items-center gap-0.5">
          {rightSlot}
        </div>

        {/* Per-environment extras: AlertDialog, LinkToCmmdSheet, etc. */}
        {children}
      </div>
    </SidebarMenuItem>
  );
});
